/**
 * /well-servicing/ai — the AI Drafts register (WS-6, fieldiq-api#1296). WT-6's
 * register, for this vertical.
 *
 * A full-width register of the company's stored AI runs (GET /ai/digests,
 * newest first, the API's latest 20), filtered by review state, by kind and by
 * job in the URL so a draft's back arrow returns to the list as it was left.
 * A row opens the draft on its own page, where it is approved (optionally
 * edited) or dismissed.
 *
 * EVERY FILTER IS THE SERVER'S. Pending / Approved / Dismissed / Superseded
 * send `outcome` (Approved = the API's `actioned`), which the API applies
 * before its 20-row limit (fieldiq-api#1307), so a filter searches the whole
 * history and returns its latest 20. "To review" is the server's pending list
 * too, shown as 20+ when it is full.
 *
 * The band runs the compliance digest (as of the tenant's today) and opens it.
 * Tour summaries, preflights and report drafts start from their own record's
 * page. Admin, office staff and supervisor only (WS_AI_ROLES); anyone else
 * gets a plain sentence and no request is made.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, FileClock, Loader2, Sparkles } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useRunWsAi, useWsAiDigests } from '../../../hooks/useWsAi';
import { useWsJobs } from '../../../hooks/useWsJobs';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wsDay } from '../../../lib/well-servicing';
import {
  WS_AI_AGENT_LABEL,
  WS_AI_OUTCOME_FILTERS,
  canUseWsAi,
  wsAiAgentLabel,
  wsAiOutcomeOf,
  wsAiOutcomeSkin,
  wsAiRefusal,
  wsAiScopeText,
} from '../../../lib/ws-ai';
import { WS_AI_AGENTS, type WsAiStoredRun } from '../../../types/ws-ai';
import { useOpenRecord } from '../../downhole/recordPage';
import {
  WS_BAND_SECONDARY,
  WS_EYEBROW,
  WS_INPUT,
  WsBadge,
  WsChip,
  WsChipRow,
  WsEmpty,
  WsField,
  WsFormError,
  WsPanel,
  WsRegister,
  type WsColumn,
} from '../WsBits';

type Row = WsAiStoredRun & { id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The company's WS jobs as a select, for the register's job filter. */
function WsJobFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const jobs = useWsJobs();
  const list = jobs.data?.data ?? [];
  return (
    <select className={WS_INPUT} aria-label="Job" value={value} onChange={(e) => onChange(e.target.value)} data-testid="ws-ai-job-filter">
      <option value="">All jobs</option>
      {list.map((j) => (
        <option key={j.id} value={j.id}>
          {j.jobNumber}
          {j.customerName ? ` · ${j.customerName}` : ''}
        </option>
      ))}
      {value && !list.some((j) => j.id === value) && <option value={value}>This job</option>}
    </select>
  );
}

export default function WsAiDraftsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const allowed = canUseWsAi(user?.role);
  const tz = useTenantTimeZone();
  const [params, setParams] = useSearchParams();
  const agentParam = params.get('agent') ?? '';
  const agent = (WS_AI_AGENTS as readonly string[]).includes(agentParam) ? agentParam : '';
  const jobParam = params.get('jobId') ?? '';
  const jobId = UUID.test(jobParam) ? jobParam : '';
  const reviewParam = params.get('review') ?? '';
  const outcome = wsAiOutcomeOf(reviewParam);
  const review = outcome ? reviewParam : '';
  const listQuery = useWsAiDigests({ agent: agent || undefined, jobId: jobId || undefined, outcome: outcome || undefined }, allowed);
  const allQuery = useWsAiDigests({}, allowed);
  const pendingQuery = useWsAiDigests({ outcome: 'pending' }, allowed);
  const run = useRunWsAi();
  const [runError, setRunError] = useState<string | null>(null);

  const returned = listQuery.data ?? [];
  const rows: Row[] = returned.map((d) => ({ ...d, id: d.agentRunId }));
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data;
  const pending = pendingQuery.isLoading || queryLoadFailure(pendingQuery) ? undefined : pendingQuery.data;
  // The API returns at most 20, so a full page means "at least 20".
  const pendingCount = pending ? (pending.length >= 20 ? '20+' : pending.length) : undefined;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const opened = (id: string | undefined) => {
    if (id) openRecord(`/well-servicing/ai/${id}`);
  };

  const runCompliance = () => {
    setRunError(null);
    run.mutate({ agent: 'ws_compliance_digest' }, {
      onSuccess: (r) => opened(r?.agentRunId),
      onError: (err) => setRunError(wsAiRefusal(err, 'The compliance digest did not run.')),
    });
  };

  const columns: WsColumn<Row>[] = [
    { key: 'kind', header: 'Draft', render: (d) => <span className="font-extrabold text-white">{wsAiAgentLabel(d.agent)}</span> },
    { key: 'covers', header: 'Covers', render: (d) => wsAiScopeText(String(d.agent), d.triggerData, wsDay) },
    { key: 'run', header: 'Run', render: (d) => formatInstant(d.createdAt, tz) ?? '—' },
    { key: 'draft', header: 'AI narrative', render: (d) => (d.draft?.narrative?.trim() ? 'Drafted' : 'Absent') },
    { key: 'status', header: 'Review', render: (d) => <WsBadge skin={wsAiOutcomeSkin(d.outcome)} value={d.outcome} /> },
  ];

  const actions = allowed ? (
    <div className="flex flex-wrap gap-2" data-testid="ws-ai-run-actions">
      <button type="button" className={WS_BAND_SECONDARY} onClick={runCompliance} disabled={run.isPending} data-testid="ws-ai-run-compliance">
        {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ClipboardCheck className="size-4" aria-hidden="true" />}
        {run.isPending ? 'Running…' : 'Run compliance digest'}
      </button>
    </div>
  ) : undefined;

  const kindWord = agent ? WS_AI_AGENT_LABEL[agent as keyof typeof WS_AI_AGENT_LABEL].toLowerCase() : 'AI draft';
  const reviewWord = review ? WS_AI_OUTCOME_FILTERS.find((f) => f.key === review)?.label.toLowerCase() : null;
  const emptyText = reviewWord
    ? `No ${agent ? `${kindWord} run` : 'AI draft'}${jobId ? ' for this job' : ''} is ${reviewWord}.`
    : agent
      ? `No ${kindWord} has been run${jobId ? ' for this job' : ''}.`
      : jobId
        ? 'No AI draft has been run for this job.'
        : 'No AI draft has been run yet. Run the compliance digest above, or draft from a tour sheet, ticket, job or report.';

  return (
    <div data-testid="ws-ai-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="AI Drafts"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions}
        stats={
          allowed
            ? [
                { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'To review', sub: 'Awaiting approval', tone: 'accent', loading: pendingQuery.isLoading, value: displayValue(pendingCount) },
                { icon: <Sparkles className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Latest 20', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(all ? all.length : undefined) },
              ]
            : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ws-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : (
            <>
              <p className="text-xs text-fieldiq-muted">
                Figures are computed exactly. The AI writes a draft explanation that waits for a person to approve or dismiss it — nothing is sent or published by the AI.
              </p>
              {runError && <WsFormError message={runError} />}
              <WsChipRow label="Review">
                <WsChip label="All" active={!review} onClick={() => setParam('review', '')} />
                {WS_AI_OUTCOME_FILTERS.map((f) => (
                  <WsChip key={f.key} label={f.label} active={review === f.key} onClick={() => setParam('review', f.key)} />
                ))}
              </WsChipRow>
              <WsChipRow label="Draft kind">
                <WsChip label="All" active={!agent} onClick={() => setParam('agent', '')} />
                {WS_AI_AGENTS.map((a) => (
                  <WsChip key={a} label={WS_AI_AGENT_LABEL[a]} active={agent === a} onClick={() => setParam('agent', a)} />
                ))}
              </WsChipRow>
              <div className="w-full sm:w-72">
                <WsField label="Job">
                  <WsJobFilter value={jobId} onChange={(v) => setParam('jobId', v)} />
                </WsField>
              </div>
              <WsPanel eyebrow="AI drafts" title="Drafts" testId="ws-ai-register">
                <WsRegister
                  query={listQuery}
                  rows={rows}
                  columns={columns}
                  rail={(d) => wsAiOutcomeSkin(d.outcome).rail}
                  onOpen={(d) => opened(d.agentRunId)}
                  subject="AI drafts"
                  testId="ws-ai-run"
                  minWidth="min-w-[760px]"
                  empty={<WsEmpty>{emptyText}</WsEmpty>}
                  card={(d) => (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-white">{wsAiAgentLabel(d.agent)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{wsAiScopeText(String(d.agent), d.triggerData, wsDay)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{formatInstant(d.createdAt, tz) ?? '—'}</p>
                      </div>
                      <WsBadge skin={wsAiOutcomeSkin(d.outcome)} value={d.outcome} />
                    </div>
                  )}
                />
              </WsPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
